import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { InnerPageContainer } from '../../InnerPageContainer/InnerPageContainer';
import { SimplifiedHeader } from '../../SimplifiedHeader/SimplifiedHeader';

const { HOME } = ROUTES;

/**
 * Optional inner container for the notification content of the unsubscribe page
 * @param children - The children to be rendered inside the container
 * @returns A React component that renders the notification content container
 */
function NotificationContainer({ children, ...props }: { children: React.ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className="flex flex-col gap-4 align-center justify-center px-5 py-23" {...props}>
      {children}
    </div>
  );
}

/**
 * Container component for the unsubscribe page, to manage the layout and header of the page
 * @param children - The children to be rendered inside the container
 * @returns A React component that renders the unsubscribe page container
 */
function UnsubscribePageContainer({ children, ...props }: ComponentProps<'div'>) {
  const t = useTranslations('unsubscribe');
  return (
    <div data-track-feature="Unsubscribe" {...props}>
      <SimplifiedHeader logoLink={HOME} showButton buttonText={t('to_site')} href={HOME} />
      <InnerPageContainer className="sm-md:pt-20 sm-md:pb-20 h-full border-border border-t sm-md:border-t-0">
        <div className="px-7 py-10 md:px-10 flex flex-col gap-7">
          <div>
            <h4 className="mb-2" data-testid="unsubscribe-title">
              {t('title')}
            </h4>
            <p data-testid="unsubscribe-description">{t('description')}</p>
          </div>
          {children}
        </div>
      </InnerPageContainer>
    </div>
  );
}

UnsubscribePageContainer.NotificationContainer = NotificationContainer;

export { UnsubscribePageContainer };
